<style lang="scss" src="./styles.scss" scoped></style>

<template>
  <div class="mobile-view">
    <header class="header">
      <div class="heading-primary">
        {{ $t('shopping.quickcart.basket') }}
      </div>
      <button ref="closeBtnRef" type="button" class="header-close iconfont-cross" aria-label="Close"></button>
    </header>
    <div class="content cart">
      <template v-if="isLoading"></template>
      <template v-else-if="hasItems">
        <div ref="adyenContainer" class="express-payment-wrapper section-border-bottom"></div>
        <CartOverview ref="cartOverviewRef" :is-click-action-enabled="true" />
        <CartVoucherBlock />
        <FreeSamplesBlock :hasBottomBorder="true" />
        <ClubPoints />
      </template>
      <div v-else class="cart-empty-state">
        <p class="cart-empty-state__message">
          {{ emptyBasketMessage }}
        </p>
        <span class="cart-empty-state__icon iconfont-cart-outline" aria-hidden="true"></span>
      </div>
    </div>
    <footer v-if="hasItems && !isLoading" class="footer">
      <PurchaseSummary @submit="handleSubmit" />
    </footer>
    <div v-if="isLoading" class="pp-in wait"></div>
  </div>
</template>

<script setup>
  import { i18n } from '@/translations/i18n'
  import { ref, computed, onMounted, onBeforeUnmount, inject, nextTick, watch } from 'vue'
  import { useRouter } from 'vue-router'
  import { useStore } from 'vuex'
  import { useI18n } from 'vue-i18n'

  import { useUser } from '@/app/Checkout/composables/useUser'
  import { useCart } from '@/app/Checkout/composables/useCart'
  import { trackScreenView } from '../../../../../helper'

  const isOverlay = inject('isOverlay', false)
  import PurchaseSummary from '@/components/PurchaseSummary/index.vue'
  import ClubPoints from '@/app/Checkout/components/mobile/ClubPoints/index.vue'
  import CartOverview from '@/app/Checkout/components/mobile/CartOverview/index.vue'
  import FreeSamplesBlock from '@/app/Checkout/components/common/FreeSamplesBlock/index.vue'
  import CartVoucherBlock from '@/app/Checkout/components/common/CartVoucherBlock/index.vue'
  import { setFormSubmitPressed } from '@/composables/useAdyen'

  const store = useStore()
  const router = useRouter()
  const { user } = useUser()
  const cartOverviewRef = ref(null)
  const adyenContainer = ref(null)
  const { items } = useCart()

  const isLoading = computed(() => {
    const cartState = store.state.cart
    return Boolean(cartState?.pending) || cartState?.cart === null
  })

  const hasItems = computed(() => {
    const c = store.state.cart?.cart
    const arr = c?.cart_items ?? c?.cart?.cart_items ?? []
    return arr.length > 0
  })

  const { t } = useI18n()
  const emptyBasketMessage = computed(() => {
    const msg = t('app.cart.empty_basket')
    return msg && msg !== 'app.cart.empty_basket' ? msg : 'YOUR BASKET IS EMPTY!'
  })

  const closeBtnRef = ref(null)
  let closeBtnHandler = null

  watch(adyenContainer, (el) => {
    if (el) {
      loadExpressButtons(el)
    }
  })

  onMounted(async () => {
    if (!user.value) {
      store.dispatch('user/getUser')
    }
    store.dispatch('cart/getCart')

    const el = closeBtnRef.value
    if (el) {
      closeBtnHandler = () => closeCart()
      el.addEventListener('click', closeBtnHandler, true)
    }
    document.addEventListener('nb-overlay-loaded', (e) => {
      if (e.detail && e.detail.targetId === 'quick-cart') {
        if (adyenContainer.value) {
          loadExpressButtons(adyenContainer.value)
        }
      }
    })
    trackScreenView('Checkout', {}, 'cart_summary')
  })

  onBeforeUnmount(() => {
    const el = closeBtnRef.value
    if (el && closeBtnHandler) {
      el.removeEventListener('click', closeBtnHandler, true)
    }
  })

  const loadExpressButtons = async (element) => {
    if (!element) return
    element.innerHTML = ''

    try {
      const $ = window.$ || window.jQuery || {}
      const response = await fetch('/de-de/home/overlay/express_payment')
      const html = await response.text()
      const $parser = $(html)
      const $paymentSection = $parser.filter('#direct-pay-section-payment-options')

      if ($paymentSection.length) {
        // 1. Insert HTML
        const titleHtml = `<h2 class="section-title">${t('shopping.basket.direct_pay')}</h2>`
        element.innerHTML = titleHtml
        element.appendChild($paymentSection[0])

        // 2. IMPORTANT: Find all scripts in the fragment and re-inject them manually
        // This forces the browser to execute them
        const scripts = element.querySelectorAll('script')
        scripts.forEach((oldScript) => {
          const newScript = document.createElement('script')
          // Copy all attributes (src, integrity, etc.)
          Array.from(oldScript.attributes).forEach((attr) => newScript.setAttribute(attr.name, attr.value))
          newScript.textContent = oldScript.textContent
          // Replace old script with the new, executable one
          oldScript.parentNode.replaceChild(newScript, oldScript)
        })

        // 3. Trigger the init function after scripts are potentially loaded
        // We add a small timeout to let the script download
        setTimeout(() => {
          if (window.nbInitPaymentButtons) {
            window.nbInitPaymentButtons($(element))
          }
        }, 500)
      }
    } catch (err) {
      console.error('Failed to load express payment buttons', err)
    }
  }

  function closeCart() {
    if (isOverlay) {
      document.dispatchEvent(new CustomEvent('quickcart:close', { bubbles: true }))
    } else {
      const match = window.location.pathname.match(/^\/([^/]+)\//)
      const locale = match ? match[1] : ''
      window.location.href = locale ? `/${locale}/` : '/'
    }
  }

  const isAuthenticated = computed(() => store.state.auth?.isAuthenticated ?? false)

  const handleSubmit = async () => {
    setFormSubmitPressed(true)
    await store.dispatch('auth/getAuthStatus')
    const locale = i18n.locale
      ? i18n.locale.toLowerCase()
      : `${window.config.lang || 'de'}-${window.config.country || 'de'}`

    if (!isAuthenticated.value) {
      const event = new CustomEvent('quickcart:login-required', {
        bubbles: true,
        cancelable: true,
        detail: { source: 'cart', action: 'checkout' },
      })

      const handledByWindow = !window.dispatchEvent(event)
      const handledByDocument = !document.dispatchEvent(event)

      if (import.meta.env.DEV) {
        setTimeout(async () => {
          if (!handledByWindow && !handledByDocument) {
            if (isOverlay) {
              await router.push({ name: 'Checkout' })
            } else {
              window.location.href = `/${locale}/checkout/purchase/checkout`
            }
          }
        }, 1500)
      }
      return
    }
    window.location.href = `/${locale}/checkout/purchase/checkout`
  }
</script>
